import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../../../utils/priceHelpers';
import InvoiceButton from '../../../../Components/InvoiceButton';
import FeeInvoicesButton from '../../../../Components/FeeInvoicesButton';
import {
  disputeKindLabel,
  disputeReasonLabel,
  disputeStatusInfo,
} from '../../../../features/contracts/constants/dispute-labels';

/** Dernier litige du contrat, renvoyé par le suivi des encaissements (montants en centimes). */
interface ContractRowDispute {
  status: string;
  reason: string;
  paymentMethodType: string | null;
  amount: number;
  withdrawnAmount: number;
}

/** Infobulle du badge « Litige » : nature, statut, motif et montant retiré. */
const disputeTooltip = (dispute: ContractRowDispute) =>
  [
    `${disputeKindLabel(dispute.paymentMethodType)} de ${formatPrice(dispute.amount)}`,
    `Statut : ${disputeStatusInfo(dispute.status).label}`,
    `Motif : ${disputeReasonLabel(dispute.reason)}`,
    dispute.withdrawnAmount > 0
      ? `${formatPrice(dispute.withdrawnAmount)} déduits des encaissements`
      : 'Rien de déduit des encaissements',
  ].join('\n');

export interface ContractsTableTotals {
  count: number;
  sellingPrice: number;
  amountReceived: number;
  amountRemaining: number;
  vendorExpectedAmount: number;
  vendorReceivedAmount: number;
  vendorRemainingAmount: number;
  platformFees: number;
  remainingFees: number;
}

interface ContractsTableProps {
  filteredContracts: any[];
  totals: ContractsTableTotals;
  hasChildren: boolean;
  societyId?: string;
  vendorName?: string;
}

export function ContractsTable({
  filteredContracts,
  totals,
  hasChildren,
  societyId = '',
  vendorName = '',
}: ContractsTableProps) {
  const navigate = useNavigate();

  const colCount = (hasChildren ? 6 : 5) + 3 + 3 + 3 + 2;

  return (
    <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-overflow-x-auto">
      <table className="tw-w-full tw-divide-y tw-divide-gray-200 tw-text-sm tw-min-w-[1000px]">
        <thead className="tw-bg-gray-50 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
          {/* Ligne des groupes de colonnes */}
          <tr className="tw-border-b tw-border-gray-200">
            <th
              colSpan={hasChildren ? 6 : 5}
              className="tw-px-1 sm:tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-bg-gray-100"
            >
              Contrat
            </th>
            <th
              colSpan={3}
              className="tw-px-1 sm:tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-text-affinicar-primary tw-bg-affinicar-lighter"
            >
              Votre facturation client
            </th>
            <th
              colSpan={3}
              className="tw-px-1 sm:tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-text-green-600 tw-bg-green-50"
            >
              Vos encaissements
            </th>
            <th
              colSpan={3}
              className="tw-px-1 sm:tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-text-red-600 tw-bg-red-50"
            >
              Vos frais de transactions
            </th>
            <th
              colSpan={2}
              className="tw-px-1 sm:tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-bg-gray-100"
            >
              Vos factures
            </th>
          </tr>
          {/* Ligne des en-têtes de colonnes */}
          <tr>
            {/* Contrat */}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              N° / Client
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Statut
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Date
            </th>
            {hasChildren && (
              <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
                Vendeur
              </th>
            )}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Garantie
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Mode
            </th>
            {/* Bénéficiaire */}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-affinicar-primary tw-uppercase tw-bg-affinicar-lighter/50">
              Total
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-affinicar-primary tw-uppercase tw-bg-affinicar-lighter/50">
              Payé
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-affinicar-primary tw-uppercase tw-bg-affinicar-lighter/50">
              Restant
            </th>
            {/* Société */}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-green-600 tw-uppercase tw-bg-green-50/50">
              À recevoir
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-green-600 tw-uppercase tw-bg-green-50/50">
              Reçu
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-green-600 tw-uppercase tw-bg-green-50/50">
              Restant
            </th>
            {/* Frais */}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-red-600 tw-uppercase tw-bg-red-50/50">
              Total
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-red-600 tw-uppercase tw-bg-red-50/50">
              Payé
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-red-600 tw-uppercase tw-bg-red-50/50">
              Restant
            </th>
            {/* Docs */}
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Frais
            </th>
            <th className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">
              Client
            </th>
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {/* Ligne de totaux */}
          {filteredContracts.length > 0 && (
            <tr className="tw-bg-gray-100 tw-font-semibold tw-border-b-2 tw-border-gray-300">
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-gray-700">
                {totals.count} contrats
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
              {hasChildren && <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>}
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
              {/* Bénéficiaire */}
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-affinicar-lighter/30">
                {formatPrice(totals.sellingPrice)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-affinicar-lighter/30">
                {formatPrice(totals.amountReceived)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-affinicar-lighter/30">
                {formatPrice(totals.amountRemaining)}
              </td>
              {/* Société */}
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-green-50/30">
                {formatPrice(totals.vendorExpectedAmount)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-green-50/30">
                {formatPrice(totals.vendorReceivedAmount)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-green-50/30">
                {formatPrice(totals.vendorRemainingAmount)}
              </td>
              {/* Frais */}
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-red-50/30">
                {formatPrice(totals.platformFees + totals.remainingFees)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-red-50/30">
                {formatPrice(totals.platformFees)}
              </td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-text-right tw-bg-red-50/30">
                {formatPrice(totals.remainingFees)}
              </td>
              {/* Docs */}
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
              <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2"></td>
            </tr>
          )}
          {filteredContracts.length === 0 ? (
            <tr>
              <td
                colSpan={colCount}
                className="tw-px-3 tw-py-12 tw-text-center tw-text-gray-500"
              >
                Aucun contrat trouvé pour cette période
              </td>
            </tr>
          ) : (
            filteredContracts.map((contract: any) => (
              <tr key={contract.id} className="hover:tw-bg-gray-50">
                {/* N° / Date */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap">
                  <div
                    className="tw-font-medium tw-text-affinicar-primary hover:tw-underline tw-cursor-pointer tw-text-xs"
                    onClick={() =>
                      navigate(`/contracts/${contract.id}`, {
                        state: {
                          from: 'vendor-financial',
                          vendorUuid: societyId,
                          vendorName: vendorName,
                        },
                      })
                    }
                  >
                    {contract.label}
                    {!!(contract as any).isUpsell && (
                      <span className="tw-ml-2 tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-indigo-100 tw-text-indigo-700">UpSell</span>
                    )}
                  </div>
                  <div className="tw-text-xs tw-text-gray-400">
                    {contract.beneficiary.firstName} {contract.beneficiary.lastName}
                  </div>
                </td>
                {/* Statut */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-center">
                  <span
                    className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${
                      contract.idStatus === 1
                        ? 'tw-bg-green-100 tw-text-green-700'
                        : contract.idStatus === 2
                        ? 'tw-bg-gray-100 tw-text-gray-600'
                        : contract.idStatus === 3
                        ? 'tw-bg-red-100 tw-text-red-700'
                        : 'tw-bg-gray-100 tw-text-gray-500'
                    }`}
                  >
                    {contract.statusLabel}
                  </span>
                  {contract.dispute && (
                    <span
                      title={disputeTooltip(contract.dispute)}
                      className="tw-ml-1 tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-orange-100 tw-text-orange-700 tw-cursor-help"
                    >
                      Litige
                    </span>
                  )}
                </td>
                {/* Date */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap">
                  <div className="tw-text-gray-900 tw-text-xs">
                    {new Date(contract.dateCreation).toLocaleDateString('fr-FR')}
                  </div>
                </td>
                {/* Vendeur (conditionnel) */}
                {hasChildren && (
                  <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap">
                    <div className="tw-text-gray-900 tw-text-xs">{contract.companyName}</div>
                  </td>
                )}
                {/* Garantie */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap">
                  <div className="tw-font-medium tw-text-gray-800 tw-text-xs">{contract.warrantyCode}</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-truncate tw-max-w-[120px]" title={contract.warrantyName}>
                    {contract.warrantyName}
                  </div>
                </td>
                {/* Mode */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap">
                  <div className="tw-text-gray-900 tw-text-xs">
                    {contract.paymentMode === 'Paiement comptant'
                      ? 'Comptant'
                      : contract.paymentMode === 'Paiement comptant agence'
                      ? 'Agence'
                      : 'Mensuel'}
                  </div>
                  {contract.installmentsPaid !== undefined && (
                    <div className="tw-text-xs tw-text-gray-400">
                      {contract.installmentsPaid}/{contract.installmentsTotal}
                    </div>
                  )}
                </td>
                {/* Bénéficiaire: Total */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-affinicar-lighter/30">
                  <div className="tw-text-xs tw-font-medium tw-text-affinicar-secondary">
                    {formatPrice(contract.sellingPrice)}
                  </div>
                </td>
                {/* Bénéficiaire: Payé */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-affinicar-lighter/30">
                  <div className="tw-text-xs tw-font-medium tw-text-affinicar-primary">
                    {formatPrice(contract.amountReceived)}
                  </div>
                </td>
                {/* Bénéficiaire: Reste */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-affinicar-lighter/30">
                  <div
                    className={`tw-text-xs tw-font-medium ${
                      contract.amountRemaining > 0 ? 'tw-text-orange-600' : 'tw-text-affinicar-primary'
                    }`}
                  >
                    {formatPrice(contract.amountRemaining)}
                  </div>
                </td>
                {/* Société: À recevoir */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-green-50/30">
                  <div className="tw-text-xs tw-font-medium tw-text-green-800">
                    {formatPrice(contract.vendorExpectedAmount)}
                  </div>
                </td>
                {/* Société: Reçu */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-green-50/30">
                  <div className="tw-text-xs tw-font-medium tw-text-green-700">
                    {formatPrice(contract.vendorReceivedAmount)}
                  </div>
                </td>
                {/* Société: Reste */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-green-50/30">
                  <div
                    className={`tw-text-xs tw-font-medium ${
                      contract.vendorRemainingAmount > 0 ? 'tw-text-orange-600' : 'tw-text-green-600'
                    }`}
                  >
                    {formatPrice(contract.vendorRemainingAmount)}
                  </div>
                </td>
                {/* Frais: Total */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-red-50/30">
                  <div className="tw-text-xs tw-font-medium tw-text-red-800">
                    {formatPrice(contract.platformFees + contract.remainingFees)}
                  </div>
                </td>
                {/* Frais: Payés */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-red-50/30">
                  <div className="tw-text-xs tw-font-medium tw-text-red-600">
                    {formatPrice(contract.platformFees)}
                  </div>
                </td>
                {/* Frais: Restants */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-right tw-bg-red-50/30">
                  <div
                    className={`tw-text-xs tw-font-medium ${
                      contract.remainingFees > 0 ? 'tw-text-orange-600' : 'tw-text-red-600'
                    }`}
                  >
                    {formatPrice(contract.remainingFees)}
                  </div>
                </td>
                {/* Docs: Frais + Contrat */}
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-center">
                  <FeeInvoicesButton
                    contractId={contract.id}
                    feeInvoiceIds={contract.feeInvoiceIds || []}
                  />
                </td>
                <td className="tw-px-1 sm:tw-px-1 sm:tw-px-2 tw-py-1 sm:tw-py-2 tw-whitespace-nowrap tw-text-center">
                  <InvoiceButton
                    contractId={contract.id}
                    invoiceType={contract.invoiceType}
                    invoiceDocumentId={contract.invoiceDocumentId}
                    hasInvoices={contract.hasInvoices ?? !!contract.invoiceDocumentId}
                    invoiceCount={contract.invoiceCount ?? (contract.invoiceDocumentId ? 1 : 0)}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
